import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getLastResults, getDetailResults } from "../../../apiservices/CasionApi";
import RefResultModal from "../refResultModal";
import { formatRefMatchTime } from "../../../utils/refMatchTime";

/**
 * Last Result items + result modal in the reference admin's markup, for the games without a
 * dedicated result UI (Cricket Match 20-20, Casino Meter, 1 Card Meter, 20-20 D T L,
 * Bollywood Casino and their virtual versions). Rendered INSIDE `.casino-video-last-results`.
 *
 *   renderItem(win) → { className, content } — the reference LastResultsSection span for the game
 *   Body            → the reference result-modal body for the game (gets the round's t1)
 *
 * Click → full-page .loader-overlay while the round detail loads, then the result modal
 * ("{ename} Result", Round-ID / Match Time + Body).
 */
export default function UserSiteLastResult({ gameType, renderItem, Body, fallbackTitle = "" }) {
  const apiType = String(gameType || "").toLowerCase();
  const [results, setResults] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const clickRef = useRef(0);

  useEffect(() => {
    if (!apiType) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getLastResults(apiType);
        const list = res?.data?.data?.res;
        if (!cancelled && Array.isArray(list)) setResults(list);
      } catch {
        /* keep previous list */
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [apiType]);

  const openResult = async (mid) => {
    if (!mid || loading) return;
    const req = ++clickRef.current;
    setLoading(true);
    try {
      const response = await getDetailResults(apiType, String(mid));
      const t1 = response?.data?.data?.t1 ?? response?.data?.t1;
      if (req === clickRef.current && t1) setDetail(t1);
    } catch (error) {
      console.error("Error fetching detail results:", error);
    }
    if (req === clickRef.current) setLoading(false);
  };

  return (
    <>
      {results.map((r, i) => {
        const { className, content } = renderItem
          ? renderItem(String(r?.win ?? ""))
          : { className: "resultb", content: String(r?.win ?? "") };
        return (
          <span key={r?.mid || i} className={className || undefined} onClick={() => openResult(r?.mid)}>
            {content}
          </span>
        );
      })}
      {loading &&
        createPortal(<div className="loader-overlay" />, document.querySelector(".page-content") || document.body)}
      <RefResultModal
        show={!!detail && !loading}
        onHide={() => setDetail(null)}
        title={`${detail?.ename || fallbackTitle} Result`}
        roundId={detail?.rid}
        matchTime={formatRefMatchTime(detail?.mtime)}
      >
        {detail && Body ? <Body t1={detail} /> : null}
      </RefResultModal>
    </>
  );
}

